'use client'

import React, { FC, ReactElement, useRef } from 'react'
import BidIcon from '@/icons/bid'
import styles from './InscriptionActionBuy.module.css'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import Loading from '@/components/circle-loading'
import BitconIcon from '@/icons/bitcoin-filled'
import classNames from 'classnames'
import { Inscription } from '@/utils/types/inscription'
import usePurchaseModal from '@/utils/hooks/usePurchaseModal'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useSatoshiPriceUSD from '@/utils/hooks/useSatoshiPriceUSD'
import useWindowSize from '@/utils/hooks/useWindowSize'
import SecureListingBadge from '@/components/secure-listing-badge'

const InscriptionActionBuy: FC<{
	inscription: Inscription
	className?: string
	contentClassName?: string
	hideText?: boolean
	title?: string
	isBRC20?: boolean
	onlyShowPrice?: boolean
	onClickBuy?: () => void
	selected?: boolean
	stackedActions?: boolean
	onClickOffer?: () => void
	offerLabel?: string
}> = (props): ReactElement => {
	const {
		onClickBuy,
		inscription,
		className: _className,
		contentClassName: _contentClassName,
		hideText,
		onlyShowPrice,
		selected
	} = props
	const { isMobile } = useWindowSize()
	const [loading, openModal] = usePurchaseModal(inscription)
	const rowRef = useRef<HTMLDivElement>(null)
	const priceBlockRef = useRef<HTMLDivElement>(null)
	const buttonMeasureRef = useRef<HTMLSpanElement>(null)

	const price = useSatoshiPrice(inscription?.escrow?.satoshi_price as any)
	const [priceUSD, _isLoadingPriceUSD] = useSatoshiPriceUSD(
		inscription?.escrow?.satoshi_price as any
	)


	return (
		<div className={classNames(styles.row, props.stackedActions && styles.stackedRow)} ref={rowRef}>
			{loading ? (
				<Loading className={styles.loading} />
			) : (
				<>
					<div className={styles.priceBlock} ref={priceBlockRef}>
						<div className={styles.priceLine}>
							<BitconIcon className={styles.priceIcon} />
							<p className={styles.priceValue}>{price}</p>
							<SecureListingBadge listing={inscription} />
						</div>
						{priceUSD && (
							<p className="text-[10px] text-muted-foreground">${priceUSD}</p>
						)}
					</div>
					{onClickBuy && !props.stackedActions && (
						<button
							type="button"
							onClick={(evt) => {
								evt.preventDefault()
								evt.stopPropagation()
								onClickBuy()
							}}
							className={classNames(
								'h-6 flex items-center justify-center gap-1 rounded-full transition-all overflow-hidden text-xs font-medium',
								styles.addButton,
								selected
									? "bg-gradient-to-r from-purple-600 to-blue-500 border border-transparent text-white"
									: "border border-transparent bg-gradient-to-r from-purple-600 to-blue-500 text-white hover:brightness-110 focus-visible:ring-2 focus-visible:ring-purple-400"
							)}
							title={selected ? 'Remove from invoice' : 'Add to invoice'}
						>
							
							<span className={styles.addButtonLabel}>Buy</span>
						</button>
					)}
					{onClickBuy && !props.stackedActions && (
						<span
							ref={buttonMeasureRef}
							className={classNames(
								'h-6 flex items-center justify-center gap-1 rounded-full border text-xs font-medium',
								styles.addButton,
								styles.measureButton
							)}
							aria-hidden="true"
						>
							
							<span className={styles.addButtonLabel}>Buy</span>
						</span>
					)}
					{props.stackedActions && onClickBuy && (
						<div className={styles.cardActions}>
							<button
								type="button"
								className={classNames(styles.cardButton, styles.buyButton, selected && styles.selectedBuy)}
								aria-pressed={!!selected}
								title={selected ? 'Remove from invoice' : 'Add to invoice'}
								onClick={(event) => {
									event.preventDefault()
									event.stopPropagation()
									onClickBuy()
								}}
							>
								Buy
							</button>
							{props.onClickOffer && (
								<button
									type="button"
									className={classNames(styles.cardButton, styles.offerButton)}
									aria-label={props.offerLabel || 'Make offer'}
									title={props.offerLabel || 'Make offer'}
									onClick={(event) => {
										event.preventDefault()
										event.stopPropagation()
										props.onClickOffer?.()
									}}
								>
									<BidIcon size={13} className={styles.offerIcon} aria-hidden="true" />
									{/* The row container picks one label by its own width, so the
									    pair never wraps: "Make offer", then "Offer", then the icon. */}
									<span className={styles.offerLong}>{props.offerLabel || 'Make offer'}</span>
									<span className={styles.offerShort}>
										{props.offerLabel === 'Cancel offer' ? 'Cancel' : 'Offer'}
									</span>
								</button>
							)}
						</div>
					)}
					{!onlyShowPrice && !hideText && (
						<ButtonGradientEpic
							onClick={(evt) => {
								if (onClickBuy) {
									return onClickBuy()
								}

								// @ts-ignore
								openModal(evt, props?.isBRC20)
							}}
							className="h-7 whitespace-nowrap cursor-pointer text-white text-sm font-semibold flex items-center justify-center px-4 rounded-full"
						>
							{props.title || isMobile ? 'Buy' : 'Buy Now'}
						</ButtonGradientEpic>
					)}
				</>
			)}
		</div>
	)
}

export default InscriptionActionBuy
